import React, { useState, useCallback } from "react";

const HierarchicalItem = React.memo(
  ({
    item,
    renderItem,
    depth = 0,
    isCollapsed,
    onToggleCollapse,
    collapseIcon,
    isParent,
    isItemCollapsed,
  }) => {
    const handleToggleCollapse = useCallback(
      (e) => {
        e.preventDefault();
        e.stopPropagation();
        onToggleCollapse(item.id);
      },
      [item.id, onToggleCollapse]
    );

    // Only show collapse icon for parent items with children
    const shouldShowCollapseIcon =
      isParent && item.children && item.children.length > 0;

    return (
      <div>
        {renderItem({
          collapseIcon: shouldShowCollapseIcon ? (
            <div
              onClick={handleToggleCollapse}
              style={{ cursor: "pointer", display: "inline-block" }}
            >
              {collapseIcon({ isCollapsed, item })}
            </div>
          ) : null,
          depth,
          handler: null,
          index: 0,
          item,
        })}

        {/* Render children if not collapsed */}
        {!isCollapsed && item.children && item.children.length > 0 && (
          <div
            style={{
              backgroundColor: depth > 0 ? "#F9F8F7" : "transparent",
              marginTop: depth > 0 ? "10px" : "0",
              paddingLeft: "20px",
            }}
          >
            <div style={{ marginLeft: depth * 20 }}>
              {item.children.map((child) => (
                <HierarchicalItem
                  key={child.id}
                  item={child}
                  renderItem={renderItem}
                  depth={depth + 1}
                  isCollapsed={isItemCollapsed(child.id)}
                  onToggleCollapse={onToggleCollapse}
                  collapseIcon={collapseIcon}
                  isParent={child.children && child.children.length > 0}
                  isItemCollapsed={isItemCollapsed}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
);

HierarchicalItem.displayName = "HierarchicalItem";

const NestableSortable = React.memo(
  ({ items, renderItem, renderCollapseIcon, className, maxDepth = 10 }) => {
    const [collapsedItems, setCollapsedItems] = useState(new Set());

    // Optimized toggle collapse function
    const toggleCollapse = useCallback((itemId) => {
      setCollapsedItems((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(itemId)) {
          newSet.delete(itemId);
        } else {
          newSet.add(itemId);
        }
        return newSet;
      });
    }, []);

    // Check if an item is collapsed
    const isItemCollapsed = useCallback(
      (itemId) => {
        return collapsedItems.has(itemId);
      },
      [collapsedItems]
    );

    return (
      <div className={className}>
        {items.map((item) => (
          <HierarchicalItem
            key={item.id}
            item={item}
            renderItem={renderItem}
            depth={item.children && item.children.length > 0 ? 1 : 0}
            isCollapsed={isItemCollapsed(item.id)}
            onToggleCollapse={toggleCollapse}
            collapseIcon={renderCollapseIcon}
            isParent={item.children && item.children.length > 0}
            isItemCollapsed={isItemCollapsed}
          />
        ))}
      </div>
    );
  }
);

NestableSortable.displayName = "NestableSortable";

export default NestableSortable;